import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import StarField from '../components/StarField';
import PhoneFrame from '../components/PhoneFrame';
import BottomNav from '../components/BottomNav';
import PostCard from '../components/PostCard';
import { GalaxyMark } from '../components/OrbitLogo';
import { createGalaxy, addFriend, removeFriend } from '../lib/orbitApi';
import { Search, Atom, Star, UserPlus, Check, Plus } from 'lucide-react';
import { toast } from 'sonner';

const TABS = ['Cosmos', 'Galaxies', 'Friends'];

export default function Home() {
  const { posts, postsLoading, loadPosts, galaxies, loadGalaxies, friends, loadFriends, authUser } = useApp();
  const nav = useNavigate();
  const [tab, setTab] = useState('Cosmos');
  const [newGalaxyName, setNewGalaxyName] = useState('');
  const [creatingGalaxy, setCreatingGalaxy] = useState(false);

  useEffect(() => {
    if (authUser) loadPosts(tab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, authUser]);

  const handleCreateGalaxy = async (e) => {
    e.preventDefault();
    if (!newGalaxyName.trim()) return;
    setCreatingGalaxy(true);
    try {
      await createGalaxy(newGalaxyName.trim());
      setNewGalaxyName('');
      await loadGalaxies();
      toast(`Created ${newGalaxyName} galaxy`);
    } catch (err) {
      toast(err.message || 'Could not create galaxy');
    } finally {
      setCreatingGalaxy(false);
    }
  };

  const toggleOrbit = async (friendHandle, friendId, currentlyOrbiting) => {
    try {
      if (currentlyOrbiting) {
        await removeFriend(friendId);
      } else {
        await addFriend(friendId);
      }
      await loadFriends();
    } catch (err) {
      toast(err.message || 'Could not update friend');
    }
  };

  return (
    <PhoneFrame withNav>
      <div className="relative min-h-full">
        <StarField />
        <div className="relative z-10">
          {/* Top bar */}
          <div className="orbit-page-header justify-between">
            <button className="press orbit-icon-button" onClick={() => nav('/search')} aria-label="Search">
              <Search />
            </button>
            <div className="floaty"><GalaxyMark size={48} /></div>
            <button className="press orbit-icon-button" onClick={() => nav('/settings')} aria-label="Settings">
              <Atom />
            </button>
          </div>

          {/* Tabs with star divider */}
          <div className="px-2 mt-1">
            <div className="relative flex items-center">
              <div className="star-line flex-1" />
              <Star size={20} fill="#e7dcff" color="#e7dcff" className="mx-2" />
              <div className="star-line flex-1" />
            </div>
            <div className="flex justify-between px-4 mt-2">
              {TABS.map((t) => (
                <button key={t} onClick={() => setTab(t)} className="press relative pb-2 font-display"
                  style={{ fontSize: 19, fontWeight: 600, color: tab === t ? '#f3ecff' : '#6f6795' }}>
                  <span className={tab === t ? 'text-glow-purple' : ''}>{t}</span>
                  {tab === t && <span className="absolute left-0 right-0 -bottom-0 h-[3px] rounded-full glow-pink" style={{ background: '#b98cff' }} />}
                </button>
              ))}
            </div>
          </div>
          <div className="h-px w-full" style={{ background: 'linear-gradient(90deg,transparent,rgba(201,162,75,.5),transparent)' }} />

          {/* Content */}
          {tab === 'Cosmos' && (
            <div>
              {postsLoading && <p className="text-center py-8 italic" style={{ color: '#6f6795' }}>Loading…</p>}
              {!postsLoading && posts.length === 0 && (
                <p className="text-center py-8 italic" style={{ color: '#6f6795' }}>Nothing here yet. Be the first to launch a thought.</p>
              )}
              {posts.map((p) => <PostCard key={p.id} post={p} />)}
            </div>
          )}

          {tab === 'Galaxies' && (
            <div className="p-4">
              <form onSubmit={handleCreateGalaxy} className="flex gap-2 mb-4">
                <input
                  value={newGalaxyName}
                  onChange={(e) => setNewGalaxyName(e.target.value)}
                  placeholder="New Galaxy name"
                  className="orbit-field flex-1 placeholder:text-[#8a83a8]"
                  style={{ minWidth: 0, minHeight: 40, background: 'rgba(255,255,255,.06)', color: '#efeaff' }}
                />
                <button type="submit" disabled={creatingGalaxy} className="press orbit-primary-button !px-4">
                  <Plus /> {creatingGalaxy ? '…' : 'Create'}
                </button>
              </form>
              {galaxies.length === 0 ? (
                <p className="text-center py-8 italic" style={{ color: '#6f6795' }}>No Galaxies yet. Create one above.</p>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  {galaxies.map((g) => (
                    <div key={g.id} onClick={() => nav(`/galaxies/${g.id}`)} className="chip orbit-surface-card text-left cursor-pointer">
                      <div className="w-9 h-9 rounded-full mb-3" style={{ background: 'radial-gradient(circle at 40% 35%, #8b7ce8, transparent 75%)', boxShadow: '0 0 16px #8b7ce8' }} />
                      <div className="font-semibold capitalize" style={{ color: '#f1ecff' }}>{g.name}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {tab === 'Friends' && (
            <div className="p-3">
              {friends.length === 0 && (
                <p className="text-center py-8 italic" style={{ color: '#6f6795' }}>No friends yet.</p>
              )}
              {friends.map((fr) => (
                <div key={fr.handle} className="orbit-surface-card flex items-center gap-3 mb-3">
                  {fr.avatar ? (
                    <img src={fr.avatar} alt="" className="w-12 h-12 rounded-full object-cover" style={{ border: '1px solid rgba(160,140,230,.4)' }} />
                  ) : (
                    <div className="w-12 h-12 rounded-full flex items-center justify-center font-bold" style={{ background: 'linear-gradient(135deg,#ff5bd1,#6b4fd6)', color: '#fff' }}>
                      {(fr.name || fr.handle || '?')[0]?.toUpperCase()}
                    </div>
                  )}
                  <div className="flex-1">
                    <div className="font-semibold" style={{ color: '#f1ecff' }}>{fr.name}</div>
                    <div className="text-sm" style={{ color: '#8079a6' }}>@{fr.handle}</div>
                  </div>
                  <button onClick={() => toggleOrbit(fr.handle, fr.id, fr.orbiting)}
                    className={`press !px-4 ${fr.orbiting ? 'orbit-secondary-button' : 'orbit-primary-button'}`}>
                    {fr.orbiting ? <><Check /> Orbiting</> : <><UserPlus /> Orbit</>}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
        <BottomNav />
      </div>
    </PhoneFrame>
  );
}
